import React, { useEffect, useState } from "react"
import { createRoot, Root } from "react-dom/client"

import Toast from "../components/shared/Toast"

type FlashMessage = {
  type: string
  content: string
}

const FLASH_TYPE_MAP: Record<string, string> = {
  success: "success",
  notice: "success",
  error: "error",
  alert: "error",
  warning: "warning",
  info: "info"
}

let flashRoot: Root | null = null
let flashContainer: HTMLDivElement | null = null
let lastProcessedPayload: string | null = null

function normalizeFlashType(type: string): string {
  return FLASH_TYPE_MAP[type] || "info"
}

function readFlashMessagesFromJson(): FlashMessage[] {
  const jsonEl = document.getElementById("flash-messages")
  if (!jsonEl?.textContent?.trim()) return []

  try {
    const parsed = JSON.parse(jsonEl.textContent) as Record<string, string>
    return Object.entries(parsed)
      .filter(([, content]) => content?.trim())
      .map(([type, content]) => ({
        type: normalizeFlashType(type),
        content: content.trim()
      }))
  } catch {
    return []
  }
}

function readFlashMessagesFromDom(): FlashMessage[] {
  const flashesEl = document.querySelector(".flashes")
  if (!flashesEl) return []

  return Array.from(flashesEl.querySelectorAll(".flash"))
    .map((el) => ({
      type: normalizeFlashType(
        el.className
          .split(/\s+/)
          .find((name) => name !== "flash")
          ?.trim() || ""
      ),
      content: el.textContent?.trim() || ""
    }))
    .filter((message) => message.content.length > 0)
}

function readFlashMessages(): FlashMessage[] {
  const fromJson = readFlashMessagesFromJson()
  if (fromJson.length > 0) return fromJson

  return readFlashMessagesFromDom()
}

function clearFlashMarkup() {
  document.getElementById("flash-messages")?.remove()
  document.querySelector(".flashes")?.remove()
}

function useFlashToastPosition(): "topCenter" | "bottomCenter" {
  const [position, setPosition] = useState<"topCenter" | "bottomCenter">(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches ? "topCenter" : "bottomCenter"
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 640px)")
    const updatePosition = () => {
      setPosition(mediaQuery.matches ? "topCenter" : "bottomCenter")
    }

    updatePosition()
    mediaQuery.addEventListener("change", updatePosition)
    return () => mediaQuery.removeEventListener("change", updatePosition)
  }, [])

  return position
}

function FlashToastsHost({ messages }: { messages: FlashMessage[] }) {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  const position = useFlashToastPosition()

  const current = messages[index]

  useEffect(() => {
    if (!visible && index + 1 < messages.length) {
      const timer = window.setTimeout(() => {
        setIndex((prev) => prev + 1)
        setVisible(true)
      }, 300)

      return () => window.clearTimeout(timer)
    }

    if (!visible && index + 1 >= messages.length) {
      const timer = window.setTimeout(() => {
        flashRoot?.unmount()
        flashRoot = null
        flashContainer?.remove()
        flashContainer = null
      }, 500)

      return () => window.clearTimeout(timer)
    }
  }, [visible, index, messages.length])

  if (!current) return null

  return (
    <Toast
      visible={visible}
      setVisible={setVisible}
      type={current.type}
      position={position}
      timeout={6000}>
      {current.content}
    </Toast>
  )
}

export function initFlashToasts() {
  const messages = readFlashMessages()
  if (messages.length === 0) return

  const payload = JSON.stringify(messages)
  if (payload === lastProcessedPayload) return
  lastProcessedPayload = payload

  clearFlashMarkup()

  if (!flashContainer) {
    flashContainer = document.createElement("div")
    flashContainer.id = "flash-toasts-root"
    document.body.appendChild(flashContainer)
    flashRoot = createRoot(flashContainer)
  }

  flashRoot?.render(<FlashToastsHost messages={messages} />)
}

function bootFlashToasts() {
  initFlashToasts()
}

document.addEventListener("DOMContentLoaded", bootFlashToasts)
document.addEventListener("turbolinks:load", bootFlashToasts)
window.addEventListener("load", bootFlashToasts)

if (document.readyState !== "loading") {
  bootFlashToasts()
}
